import React, { useState, useEffect, useRef } from 'react';

const StatsCounter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({
    vehicles: 0,
    experience: 0,
    satisfaction: 0,
    services: 0
  });
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const targets = {
      vehicles: 2000,
      experience: 5,
      satisfaction: 98,
      services: 1000
    };

    const duration = 2000; // 2 seconds
    const steps = 60;
    const interval = duration / steps;

    const incrementValues = {
      vehicles: targets.vehicles / steps,
      experience: targets.experience / steps,
      satisfaction: targets.satisfaction / steps,
      services: targets.services / steps
    };

    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      
      setCounts({
        vehicles: Math.min(Math.floor(incrementValues.vehicles * currentStep), targets.vehicles),
        experience: Math.min(Math.floor(incrementValues.experience * currentStep), targets.experience),
        satisfaction: Math.min(Math.floor(incrementValues.satisfaction * currentStep), targets.satisfaction),
        services: Math.min(Math.floor(incrementValues.services * currentStep), targets.services)
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setCounts(targets);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [isVisible]);

  const stats = [
    {
      number: counts.vehicles,
      suffix: '+',
      label: 'Véhicules reprogrammés',
      icon: '🚗',
      color: 'from-red-600 to-red-700'
    },
    {
      number: counts.experience,
      suffix: ' ans',
      label: "D'expérience",
      icon: '⚡',
      color: 'from-orange-600 to-orange-700'
    },
    {
      number: counts.satisfaction,
      suffix: '%',
      label: 'Clients satisfaits',
      icon: '⭐',
      color: 'from-yellow-600 to-yellow-700'
    },
    {
      number: counts.services,
      suffix: '+',
      label: 'Interventions réalisées',
      icon: '🔧',
      color: 'from-green-600 to-green-700'
    }
  ];

  return (
    <section ref={sectionRef} className="py-20 px-4 bg-slate-900/50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Nos chiffres parlent d'eux-mêmes
          </h2>
          <p className="text-gray-400 text-lg">
            La confiance de milliers de clients depuis 5 ans
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group relative bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border-2 border-slate-700 hover:border-red-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-red-600/20"
              style={{
                animation: isVisible ? `fadeInUp 0.6s ease-out ${index * 0.1}s both` : 'none'
              }}
            >
              {/* Glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
              
              <div className="relative">
                <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {stat.icon}
                </div>
                <div className={`text-5xl md:text-6xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}>
                  {stat.number}{stat.suffix}
                </div>
                <p className="text-gray-400 text-sm md:text-base font-medium">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default StatsCounter;
